import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight, Mail, Phone, MapPin, Linkedin, MessageCircle } from 'lucide-react';
import { COMPANY, NAV, FOCUS_AREAS } from '../data/content';
import { openExternal } from '../lib/openExternal';

export default function Footer() {
  const loc = useLocation();
  if (loc.pathname.startsWith('/admin')) return null;
  return (
    <footer className="relative bg-[var(--ink)] text-white overflow-hidden">
      <div className="absolute inset-0 grid-bg-dark opacity-50 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[var(--orange)]/8 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[var(--orange)]/10 blur-3xl pointer-events-none" />

      <div className="relative px-6 md:px-10 lg:px-14 pt-24 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-14 max-w-[1500px] mx-auto">
          <div className="md:col-span-5">
            <img src="/logos/full.png" alt="Tricolite" className="h-16 md:h-20 w-auto mb-8" style={{filter:'brightness(1.15)'}}/>
            <h3 className="font-display text-[42px] md:text-[56px] leading-[1.02] font-bold mb-6" style={{color:'#fff'}}>
              Partner<br/>
              <span className="font-serif-it text-[var(--orange)]">with us.</span>
            </h3>
            <Link to="/contact" className="inline-flex items-center gap-3 group">
              <span className="w-12 h-12 rounded-full bg-[var(--orange)] flex items-center justify-center group-hover:scale-110 transition-transform">
                <ArrowUpRight className="w-5 h-5 text-white"/>
              </span>
              <span className="font-medium tracking-wide">Start a Conversation</span>
            </Link>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mb-5">Navigate</div>
            <ul className="space-y-3">
              {NAV.map(n => (
                <li key={n.path}>
                  <Link to={n.path} className="text-white/80 hover:text-[var(--orange)] transition-colors link-u text-[15px]">{n.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mb-5">Reach Us</div>
            <ul className="space-y-4 text-[15px]">
              <li className="flex items-start gap-3"><MapPin className="w-4 h-4 text-[var(--orange)] mt-1 shrink-0"/><span className="text-white/80">216, Success Towers,<br/>Golf Course Extension Road,<br/>Gurugram, Haryana – 122018, India</span></li>
              <li className="flex items-start gap-3"><Mail className="w-4 h-4 text-[var(--orange)] mt-1 shrink-0"/><a className="text-white/80 hover:text-white link-u" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
              <li className="flex items-start gap-3"><Phone className="w-4 h-4 text-[var(--orange)] mt-1 shrink-0"/><a className="text-white/80 hover:text-white link-u" href={`tel:${COMPANY.phoneTel}`}>{COMPANY.phonePrimary}</a></li>
              <li className="flex items-start gap-3"><MessageCircle className="w-4 h-4 text-[var(--orange)] mt-1 shrink-0"/><a className="text-white/80 hover:text-white link-u" href={COMPANY.whatsapp} onClick={openExternal(COMPANY.whatsapp)} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-number">{COMPANY.whatsappDisplay} <span className="text-white/40 text-[12px]">(WhatsApp)</span></a></li>
            </ul>
            <div className="flex items-center gap-3 mt-6">
              <a href={COMPANY.linkedin} onClick={openExternal(COMPANY.linkedin)} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center hover:bg-[var(--orange)] hover:border-[var(--orange)] transition-colors"><Linkedin className="w-4 h-4"/></a>
              <a href={COMPANY.whatsapp} onClick={openExternal(COMPANY.whatsapp)} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center hover:bg-[var(--orange)] hover:border-[var(--orange)] transition-colors"><MessageCircle className="w-4 h-4"/></a>
              <a href="https://www.google.com/maps/search/?api=1&query=216%20Success%20Towers%2C%20Golf%20Course%20Extension%20Road%2C%20Gurugram%2C%20Haryana%20122018" onClick={openExternal("https://www.google.com/maps/search/?api=1&query=216%20Success%20Towers%2C%20Golf%20Course%20Extension%20Road%2C%20Gurugram%2C%20Haryana%20122018")} target="_blank" rel="noopener noreferrer" aria-label="Google Maps" className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center hover:bg-[var(--orange)] hover:border-[var(--orange)] transition-colors"><MapPin className="w-4 h-4"/></a>
              <a href={`tel:${COMPANY.phoneTel}`} aria-label="Phone" className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center hover:bg-[var(--orange)] hover:border-[var(--orange)] transition-colors"><Phone className="w-4 h-4"/></a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap gap-2 max-w-[1500px] mx-auto justify-center text-center">
          {FOCUS_AREAS.map((f,i) => (
            <span key={i} className="text-[12px] uppercase tracking-wider text-white/40 after:content-['•'] after:mx-3 last:after:content-none">{f}</span>
          ))}
        </div>

        <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[12px] text-white/40 max-w-[1500px] mx-auto">
          <div>© {new Date().getFullYear()} {COMPANY.legal}. All rights reserved.</div>
          <div className="flex gap-6 items-center">
            <span>Managed by Abacus Desk</span>
            <span>ISO 9001 · 14001 · 45001 Certified</span>
            <Link to="/admin" className="opacity-50 hover:opacity-100">Admin</Link>
          </div>
        </div>

        {/* Group brands — sister companies under the Trilok Chand Group */}
        <div className="mt-14 pt-10 border-t border-white/10 max-w-[1500px] mx-auto" data-testid="group-brands">
          <div className="text-[11px] uppercase tracking-[0.28em] text-white/40 mb-8 text-center">EPC arm of Tricolite</div>
          <div className="flex flex-col md:grid md:grid-cols-3 items-center max-w-[820px] mx-auto gap-y-10 md:gap-x-10">
            {/* LEFT — Twelvesols → Solar Survey */}
            <Link to="/services?survey=solar" className="group flex flex-col items-center justify-center gap-2" aria-label="Twelvesols — Request a solar survey" data-testid="brand-twelvesols-link">
              <div className="h-20 md:h-24 flex items-center justify-center w-full">
                <img
                  src="/logos/twelvesols.png"
                  alt="Twelvesols"
                  className="h-9 md:h-10 w-auto max-w-[160px] md:max-w-[200px] object-contain transition-all duration-300 group-hover:scale-110 group-hover:drop-shadow-[0_0_18px_rgba(243,118,58,0.55)]"
                  data-testid="brand-twelvesols"
                />
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--orange)] md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">Solar Survey →</span>
            </Link>

            {/* CENTER — Tricolite → Home */}
            <Link to="/" className="group flex flex-col items-center justify-center gap-2" aria-label="Tricolite Energy Solutions — Home" data-testid="brand-tricolite-link">
              <div className="h-20 md:h-24 flex items-center justify-center w-full">
                <img
                  src="/logos/full.png"
                  alt="Tricolite Energy Solutions"
                  className="h-16 md:h-20 w-auto max-w-[240px] md:max-w-[320px] object-contain transition-all duration-300 group-hover:scale-105 group-hover:drop-shadow-[0_0_22px_rgba(243,118,58,0.6)]"
                  style={{filter:'brightness(1.15)'}}
                  data-testid="brand-tricolite"
                />
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--orange)] md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">Home →</span>
            </Link>

            {/* RIGHT — Energy Doctor → Energy Audit */}
            <Link to="/services?survey=energy" className="group flex flex-col items-center justify-center gap-2" aria-label="Energy Doctor — Request an energy audit" data-testid="brand-energy-doctor-link">
              <div className="h-20 md:h-24 flex items-center justify-center w-full">
                <img
                  src="/logos/energy-doctor.png"
                  alt="Energy Doctor"
                  className="h-9 md:h-10 w-auto max-w-[160px] md:max-w-[200px] object-contain transition-all duration-300 group-hover:scale-110 group-hover:drop-shadow-[0_0_18px_rgba(243,118,58,0.55)]"
                  style={{filter:'brightness(1.15)'}}
                  data-testid="brand-energy-doctor"
                />
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--orange)] md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">Energy Audit →</span>
            </Link>
          </div>
        </div>

        <div className="mt-10 flex justify-center max-w-[1500px] mx-auto">
          <img src="/logos/motto.png" alt="योगः कर्मसु कौशलम्" className="h-8 md:h-10 w-auto opacity-70" style={{filter:'brightness(1.1)'}}/>
        </div>
      </div>
    </footer>
  );
}
